import { format, isValid, parseISO } from "date-fns";
import {
  formatDateWithRelevantYear,
  formatLocalizedDate,
  formatLocalizedTime,
} from "#/shared/lib/helpers/localized-date";
import { parseOffsetDateTime } from "#ce/utils/offset-date";

/**
 * Returns an entry's `date` as a local calendar date: `"2026-09-01"` → 1 September 2026, 00:00 local.
 * A Date instance passes through unchanged.
 *
 * @param {String|Date} date
 * @returns {Date}
 */
export function toEntryDate(date) {
  return date instanceof Date ? date : parseISO(date);
}

/**
 * Returns the day as `yyyy-MM-dd`, whatever form `date` arrived in.
 *
 * @param {String|Date} date
 * @returns {String}
 */
export function toDayKey(date) {
  return format(toEntryDate(date), "yyyy-MM-dd");
}

/**
 * Returns a slot's start time as the clinic's wall-clock time in the active locale:
 * `"2026-09-01T14:30:00-05:00"` → `"2:30 PM"`. Returns `""` for an unparseable time.
 *
 * @param {String|Date} startTime
 * @returns {String}
 */
export function formatSlotTime(startTime) {
  const parsed = parseOffsetDateTime(startTime);

  if (!isValid(parsed)) {
    return "";
  }

  return formatLocalizedTime(parsed);
}

/**
 * Returns the `sp-icon` name marking a slot's visit type, or `undefined` for a slot without one.
 * `visit_type_categories` is optional and may arrive as a non-array.
 *
 * @param {Object} slot
 * @returns {String|undefined}
 */
export function visitTypeIcon(slot) {
  const categories = slot.visit_type_categories;

  if (Array.isArray(categories) && categories.includes("video")) {
    return "video-visit-filled";
  }

  return undefined;
}

/**
 * Returns the abbreviated month and day: `"Sep 1"`, with the year when `showYear` asks for it.
 *
 * @param {Date} date
 * @param {String} showYear "never", "next_year" or "always"
 * @returns {String}
 */
export function formatShortDate(date, showYear) {
  return formatDateWithRelevantYear(date, { month: "short", day: "numeric" }, showYear);
}

/**
 * Returns the weekday: `"Tuesday"`, or `"Tue"` when `short`.
 *
 * @param {Date} date
 * @param {Boolean} short
 * @returns {String}
 */
export function formatWeekday(date, short) {
  return formatLocalizedDate(date, { weekday: short ? "short" : "long" });
}
